import { createAsyncThunk } from '@reduxjs/toolkit';
import { format } from 'date-fns';
import axiosInstance from 'utils/axios';

export const fetchCheckoutOrder = createAsyncThunk('orders/checkoutOrder', async ({ page, order }, thunkAPI) => {
  try {
    const res = await axiosInstance.get(
      `/orders?page=${page}&limit=10&status=CHECKOUT&order_by=created_at&order_seq=${order}`,
    );

    return res.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

export const fetchReconfirmationOrder = createAsyncThunk(
  'orders/reconfirmationOrder',
  async ({ page, order }, thunkAPI) => {
    try {
      const res = await axiosInstance.get(
        `/orders?page=${page}&limit=10&status=RECONFIRMATION&order_by=created_at&order_seq=${order}`,
      );

      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const fetchPaidOrder = createAsyncThunk('orders/paidOrder', async ({ page, order }, thunkAPI) => {
  try {
    const res = await axiosInstance.get(
      `/orders?page=${page}&limit=10&status=PAID&order_by=created_at&order_seq=${order}`,
    );

    return res.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

export const fetchOngoingOrder = createAsyncThunk('orders/ongoingOrder', async ({ page, order }, thunkAPI) => {
  try {
    const res = await axiosInstance.get(
      `/orders?page=${page}&limit=10&status=COMPLETED&order_by=created_at&order_seq=${order}`,
    );

    return res.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

export const fetchFinishedOrder = createAsyncThunk('orders/finishedOrder', async ({ page, order }, thunkAPI) => {
  try {
    const res = await axiosInstance.get(
      `/orders?page=${page}&limit=10&status=FINISHED&order_by=created_at&order_seq=${order}`,
    );

    return res.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

export const changeOrderStatus = createAsyncThunk('orders/processOrder', async (payload, thunkAPI) => {
  const data =
    payload.nextStatus === 'COMPLETED'
      ? payload?.airport_package_vehicle_id
        ? {
            order_status: payload.nextStatus,
            courier_id: payload.courier_id,
            airport_package_vehicle_id: payload.airport_package_vehicle_id,
          }
        : { order_status: payload.nextStatus, courier_id: payload.courier_id }
      : { order_status: payload.nextStatus };

  const config = {
    method: 'put',
    url: `/orders/${payload.transaction_key}`,
    data,
  };

  try {
    const response = await axiosInstance(config);

    const params = {
      page: 1,
      order: 'DESC',
    };

    switch (payload.prevStatus) {
      case 'CHECKOUT':
        thunkAPI.dispatch(fetchCheckoutOrder(params));
        break;
      case 'PAID':
        thunkAPI.dispatch(fetchPaidOrder(params));
        break;
      case 'COMPLETED':
        thunkAPI.dispatch(fetchOngoingOrder(params));
        break;
    }

    return response.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response?.data?.data?.message || error.message);
  }
});

export const rejectOrder = createAsyncThunk('orders/rejectOrder', async (payload, thunkAPI) => {
  const data = { order_status: 'REJECTED', message: payload.message };

  const config = {
    method: 'put',
    url: `/orders/${payload.transaction_key}`,
    data,
  };

  try {
    const response = await axiosInstance(config);

    thunkAPI.dispatch(
      fetchCheckoutOrder({
        page: 1,
        order: 'DESC',
      }),
    );

    return response.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response?.data?.data?.message || error.message);
  }
});

export const rejectBankTransferPayment = createAsyncThunk('orders/rejectPayment', async (payload, thunkAPI) => {
  const data = { order_status: 'RECONFIRMATION', message: payload.message };

  const config = {
    method: 'put',
    url: `/orders/${payload.transaction_key}`,
    data,
  };

  try {
    const response = await axiosInstance(config);

    thunkAPI.dispatch(
      fetchCheckoutOrder({
        page: 1,
        order: 'DESC',
      }),
    );
    return response.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response?.data?.data?.message || error.message);
  }
});

export const fetchDepositDetail = createAsyncThunk('orders/fetchDepositDetail', async (transaction_key, thunkAPI) => {
  try {
    const res = await axiosInstance.get(`/admin/orders/${transaction_key}/deposits`);

    return res.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

export const fetchOrderById = createAsyncThunk('orders/fetchOrderById', async (transaction_key, thunkAPI) => {
  try {
    const res = await axiosInstance.get(`/admin/orders/${transaction_key}`);

    return res.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

export const fetchOrderByIdTaskAdmin = createAsyncThunk(
  'orders/fetchOrderByIdTaskAdmin',
  async ({ transaction_key, approval_id }, thunkAPI) => {
    try {
      const res = await axiosInstance.get(`/admin/orders/${transaction_key}?approval_id=${approval_id}`);

      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const fetchOrderByIdWithApproval = createAsyncThunk(
  'orders/fetchOrderByIdWithApproval',
  async (transaction_key, thunkAPI) => {
    try {
      const res = await axiosInstance.get(`/admin/orders/${transaction_key}?check_approval=1`);

      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const createDepositConfirmation = createAsyncThunk(
  'orders/createDepositConfirmation',
  async (payload, thunkAPI) => {
    try {
      const res = await axiosInstance.post(`/deposit-confirmations`, { ...payload });

      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

// GET CANCELLED ORDER
export const fetchCancelledOrder = createAsyncThunk('orders/fetchCancelledOrder', async ({ page, order }, thunkAPI) => {
  try {
    const res = await axiosInstance.get(
      `/orders?page=${page}&limit=10&status=CANCELLED&order_by=created_at&order_seq=${order}`,
    );

    return res.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

// GET CANCELLED ORDER DETAIL / ORDER CANCELATION
export const fetchOrderCancelation = createAsyncThunk(
  'orders/fetchOrderCancelation',
  async (transaction_key, thunkAPI) => {
    try {
      const res = await axiosInstance.get(`/admin/orders/${transaction_key}/cancelation`);

      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

// UPDATE ORDER CANCELATION
export const updateOrderCancelation = createAsyncThunk('orders/updateOrderCancelation', async (payload, thunkAPI) => {
  const { transaction_key, status } = payload;

  try {
    const res = await axiosInstance.put(`/admin/orders/${transaction_key}/cancelation`, {
      status,
    });

    thunkAPI.dispatch(fetchCancelledOrder({ page: 1, order: 'DESC' }));

    return res.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

// CREATE ORDER
export const createOrder = createAsyncThunk('orders/createOrder', async (payload, thunkAPI) => {
  try {
    const response = await axiosInstance.post(`/admin/orders`, { ...payload });

    const data = await response.data;

    return data.data.order.transaction_key;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

// UPLOAD FILE KTP OR SIM
export const uploadFile = createAsyncThunk('orders/uploadFile', async (payload, thunkAPI) => {
  const { file, name, documentType } = payload;

  let url = `/profile/document`;

  if (documentType) {
    url += `?documentType=${documentType}`;
  }

  try {
    const response = await axiosInstance.post(
      url,
      { file },
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );

    return {
      [name]: response.data?.file,
    };
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

// GET ORDER SUMMARY
export const fetchSummaryOrder = createAsyncThunk('orders/fetchSummaryOrder', async (payload, thunkAPI) => {
  try {
    const response = await axiosInstance.post(`/admin/orders/summary`, { ...payload });

    return response.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response?.data?.data?.message || error.message);
  }
});

// CREATE DISBURSEMENTS
export const postDisbursements = createAsyncThunk('order/postDisbursements', async (payload = {}, thunkAPI) => {
  try {
    const response = await axiosInstance.post(`/disbursements`, payload);

    /**
     * uncomment this if want to use payment using credit/debit card
     * and then make sure payment using manual transfer works just fine
     */

    // if (
    //   Object.prototype.hasOwnProperty.call(
    //     response.data.data.disbursement,
    //     'redirect_url'
    //   )
    // ) {
    //   window.open(response.data.data.disbursement.redirect_url);
    // }

    return response.data;
  } catch (error) {
    return thunkAPI.rejectWithValue('failed to create disbursement');
  }
});

// UPDATE ORDER CANCELATION
export const updateViolationOrder = createAsyncThunk('orders/updateViolationOrder', async (payload, thunkAPI) => {
  const { transaction_key, violations, total_payment } = payload;

  try {
    const res = await axiosInstance.put(`/orders/${transaction_key}`, {
      violations: violations,
      total_payment,
    });

    return res.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

export const deleteOrderById = createAsyncThunk('orders/deleteOrderById', async (transaction_key, thunkAPI) => {
  try {
    const res = await axiosInstance.delete(`/orders/${transaction_key}`);

    return res.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

export const changePaymentType = createAsyncThunk('orders/changePaymentType', async (transactionKey, thunkAPI) => {
  const config = {
    method: 'patch',
    url: `/orders/${transactionKey}/dp`,
  };

  try {
    await axiosInstance(config);
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

export const fetchWithoutDriverOrder = createAsyncThunk(
  'orders/fetchWithoutDriverOrder',
  async (
    {
      page,
      order = 'DESC',
      status,
      orderBy = 'created_at',
      endDate,
      startDate,
      vehicleBrand,
      vehicleId,
      locationId,
      user_name,
      timeZoneIdentifier,
    },
    thunkAPI,
  ) => {
    let mainUrl = `/orders?page=${page}&limit=10&without_driver=1&order_type_id=1`;

    if (status !== undefined) {
      status.map((item) => {
        mainUrl += `&status[]=${item}`;
      });
    }

    if (locationId !== undefined) {
      locationId.map((item) => {
        mainUrl += `&location_id=${item}`;
      });
    }

    if (timeZoneIdentifier !== undefined) {
      mainUrl += `&time_zone_identifier=${timeZoneIdentifier}`;
    }

    if (order) {
      mainUrl += `&order_seq=${order}`;
    }

    if (orderBy) {
      mainUrl += `&order_by=${orderBy}`;
    }

    if (startDate) {
      mainUrl += `&start_date=${startDate}`;
    }

    if (endDate) {
      mainUrl += `&end_date=${endDate}`;
    }

    if (vehicleBrand) {
      mainUrl += `&vehicle_brand=${vehicleBrand}`;
    }

    if (vehicleId && vehicleId.length !== 0) {
      vehicleId.forEach((id) => {
        mainUrl += `&vehicle_id=${id}`;
      });
    }

    if (user_name !== undefined) {
      mainUrl += `&user_name=${user_name}`;
    }

    try {
      const res = await axiosInstance.get(mainUrl);

      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const fetchWithDriverOrder = createAsyncThunk(
  'orders/fetchWithDriverOrder',
  async (
    {
      page,
      order = 'DESC',
      status,
      orderBy = 'created_at',
      endDate,
      startDate,
      vehicleBrand,
      vehicleId,
      locationId,
      user_name,
      timeZoneIdentifier,
    },
    thunkAPI,
  ) => {
    let mainUrl = `/orders?page=${page}&limit=10&with_driver=1&order_type_id=1`;

    if (order) {
      mainUrl += `&order_seq=${order}`;
    }

    if (orderBy) {
      mainUrl += `&order_by=${orderBy}`;
    }

    if (startDate) {
      mainUrl += `&start_date=${startDate}`;
    }

    if (endDate) {
      mainUrl += `&end_date=${endDate}`;
    }

    if (vehicleBrand) {
      mainUrl += `&vehicle_brand=${vehicleBrand}`;
    }

    if (vehicleId && vehicleId.length !== 0) {
      vehicleId.forEach((id) => {
        mainUrl += `&vehicle_id=${id}`;
      });
    }

    if (status !== undefined) {
      status.map((item) => {
        mainUrl += `&status[]=${item}`;
      });
    }

    if (locationId !== undefined) {
      locationId.map((item) => {
        mainUrl += `&location_id=${item}`;
      });
    }

    if (timeZoneIdentifier !== undefined) {
      mainUrl += `&time_zone_identifier=${timeZoneIdentifier}`;
    }

    if (user_name !== undefined) {
      mainUrl += `&user_name=${user_name}`;
    }

    try {
      const res = await axiosInstance.get(mainUrl);

      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const fetchOrderBookingZoneById = createAsyncThunk(
  'orders/fetchOrderBookingZoneById',
  async (transaction_key, thunkAPI) => {
    try {
      const res = await axiosInstance.get(`/orders/${transaction_key}/booking-zones`);

      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const fetchAirportTransferOrder = createAsyncThunk(
  'orders/fetchAirportTransferOrder',
  async (
    {
      page,
      order = 'DESC',
      status,
      orderBy = 'created_at',
      endDate,
      startDate,
      vehicleBrand,
      // vehicleId,
      vehicleCategoryId,
      locationId,
      user_name,
      timeZoneIdentifier,
    },
    thunkAPI,
  ) => {
    let mainUrl = `/orders?page=${page}&limit=10&order_type_id=2&order_type_id=7`;

    if (status !== undefined) {
      status.map((item) => {
        mainUrl += `&status[]=${item}`;
      });
    }

    if (timeZoneIdentifier !== undefined) {
      mainUrl += `&time_zone_identifier=${timeZoneIdentifier}`;
    }

    if (locationId !== undefined) {
      locationId.map((item) => {
        mainUrl += `&location_id=${item}`;
      });
    }

    if (user_name !== undefined) {
      mainUrl += `&user_name=${user_name}`;
    }

    if (order) {
      mainUrl += `&order_seq=${order}`;
    }

    if (orderBy) {
      mainUrl += `&order_by=${orderBy}`;
    }

    if (startDate) {
      mainUrl += `&start_date=${startDate}`;
    }

    if (endDate) {
      mainUrl += `&end_date=${endDate}`;
    }

    if (vehicleBrand) {
      mainUrl += `&vehicle_brand=${vehicleBrand}`;
    }

    // if (vehicleId && vehicleId.length !== 0) {
    //   vehicleId.forEach((id) => {
    //     mainUrl += `&vehicle_id=${id}`;
    //   });
    // }

    if (vehicleCategoryId && vehicleCategoryId.length !== 0) {
      vehicleCategoryId.forEach((id) => {
        mainUrl += `&vehicle_category_id=${id}`;
      });
    }

    try {
      const res = await axiosInstance.get(mainUrl);

      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const getAirportPackageById = createAsyncThunk('vehicle/getAirportPackageById', async (payload, thunkAPI) => {
  const { id, pickupTrip } = payload;

  let mainUrl = `/vehicle-airport-packages/${id}`;

  if (pickupTrip) {
    mainUrl = mainUrl + `?pickup_trip=${pickupTrip}`;
  }

  try {
    const response = await axiosInstance.get(mainUrl);
    return response.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

export const getVehicleAirportTransferPackage = createAsyncThunk(
  'vehicle/getVehicleAirportTransferPackage',
  async (payload, thunkAPI) => {
    const { id, limit = 10, page = 0, locationRental, pickupTrip } = payload;

    let mainUrl = `/vehicle-airport-packages/${id}/vehicles?limit=${limit}&page=${page}`;

    if (pickupTrip) {
      mainUrl = mainUrl + `&pickup_trip=${pickupTrip}`;
    }

    if (locationRental) {
      mainUrl = mainUrl + `&location_id=${locationRental}`;
    }

    try {
      const response = await axiosInstance.get(mainUrl);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const fetchSummaryAirportTransferOrder = createAsyncThunk(
  'orders/fetchSummaryAirportTransferOrder',
  async (payload, thunkAPI) => {
    try {
      const response = await axiosInstance.post(`/admin/orders/summary`, { ...payload });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const fetchDepositOrder = createAsyncThunk(
  'orders/fetchDepositOrder',
  async ({ page, order, status }, thunkAPI) => {
    let mainUrl = `/deposits?page=${page}&limit=10&order_by=created_at&order_seq=${order}`;

    if (status !== undefined) {
      status.map((item) => {
        mainUrl += `&status[]=${item}`;
      });
    }

    try {
      const res = await axiosInstance.get(mainUrl);

      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const updateDepositStatus = createAsyncThunk('orders/updateDepositStatus', async (payload, thunkAPI) => {
  const { id, status, debt, proof_of_debt_transfer, amount_to_transfer } = payload;

  try {
    const res = await axiosInstance.put(`/admin/deposits/${id}`, {
      status,
      debt,
      proof_of_debt_transfer,
      amount_to_transfer,
    });

    thunkAPI.dispatch(fetchCancelledOrder({ page: 1, order: 'DESC' }));

    return res.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

export const getDepositReports = createAsyncThunk('orders/getDepositReports', async ({ status }, thunkAPI) => {
  let mainUrl = `/deposit-reports`;

  if (status !== undefined) {
    status.map((item, idx) => {
      mainUrl += `${idx == 0 ? '?' : '&'}status[]=${item}`;
    });
  } else if (status === undefined) {
    mainUrl += `?status[]=CREATED&status[]=PROCESSED&status[]=ON_DEBT&status[]=TRANSFERED`;
  }

  try {
    const res = await axiosInstance.get(mainUrl, {
      responseType: 'blob',
    });
    const dateNow = format(new Date(), 'MM-dd-yyyy-hh-mm-ss');
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = `deposit-reports-${dateNow}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

export const importDepositReports = createAsyncThunk('orders/importDepositReports', async (file, thunkAPI) => {
  try {
    await axiosInstance.patch(`/deposit-reports`, file, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

// GET ORDER SUMMARY CUSTOMER
export const fetchSummaryCustomerOrder = createAsyncThunk(
  'orders/fetchSummaryCustomerOrder',
  async (payload, thunkAPI) => {
    try {
      const response = await axiosInstance.post(`/orders/summary`, { ...payload });

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.data?.message || error.message);
    }
  },
);

export const getOrderAddOnsById = createAsyncThunk('orders/getOrderAddOnsById', async (transaction_key, thunkAPI) => {
  try {
    const res = await axiosInstance.get(`/orders/${transaction_key}/addons`);

    return res.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

export const fetchCarCharterOrder = createAsyncThunk(
  'orders/fetchCarCharterOrder',
  async (
    {
      page,
      order = 'DESC',
      status,
      orderBy = 'created_at',
      endDate,
      startDate,
      vehicleBrand,
      vehicleId,
      user_name,
      timeZoneIdentifier,
    },
    thunkAPI,
  ) => {
    let mainUrl = `/orders?page=${page}&limit=10&order_type_id=8`;

    if (status !== undefined) {
      status.map((item) => {
        mainUrl += `&status[]=${item}`;
      });
    }

    if (timeZoneIdentifier !== undefined) {
      mainUrl += `&time_zone_identifier=${timeZoneIdentifier}`;
    }

    if (order) {
      mainUrl += `&order_seq=${order}`;
    }

    if (orderBy) {
      mainUrl += `&order_by=${orderBy}`;
    }

    if (startDate) {
      mainUrl += `&start_date=${startDate}`;
    }

    if (endDate) {
      mainUrl += `&end_date=${endDate}`;
    }

    if (vehicleBrand) {
      mainUrl += `&vehicle_brand=${vehicleBrand}`;
    }

    if (vehicleId && vehicleId.length !== 0) {
      vehicleId.forEach((id) => {
        mainUrl += `&vehicle_id=${id}`;
      });
    }

    if (user_name !== undefined) {
      mainUrl += `&user_name=${user_name}`;
    }

    try {
      const res = await axiosInstance.get(mainUrl);

      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const getOrderDisbursementById = createAsyncThunk(
  'orders/getOrderDisbursementById',
  async (transaction_key, thunkAPI) => {
    try {
      const res = await axiosInstance.get(`/orders/${transaction_key}/disbursement`);

      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);
